// COBRO EXPRESS — saldar un adeudo con UN clic, sin abrir el modal.
//
// Por qué existe (Carlos, 7-ago-2026, comparando con Kydemy): allá el cobro es «monto ya puesto
// + 3 botones de método» y se acabó. Aquí cualquier cobro abría PaymentForm, un modal de ~10
// campos (alumno, tipo, método, monto, fecha, concepto, meses, comentario, descuento, parcial).
// Para el caso normal —una alumna llega al mostrador y paga lo que debe— eso es teclear diez
// veces lo que el sistema ya sabe.
//
// 🚨 ESTO NO REEMPLAZA A PaymentForm: el modal sigue siendo el que puede editar, abonar de más,
// cobrar sin adeudo previo, vitrina, retrofechar y aplicar descuentos. Este componente cubre
// SÓLO el camino corto y delega en él todo lo demás (botón «Otro…»).
//
// El backend ya lo soportaba entero, no hizo falta tocarlo (verificado en letsmove-finances.js):
//   · con `debt_id` el alumno se adopta de la deuda → no hay que mandar account_id
//   · al cubrirse el saldo hace `UPDATE letsmove_debts SET settled=1` solo
//   · la caja es opcional (`openBox?.cashbox_id || null`): sin corte abierto NO se bloquea
//   · responde `debt_settled` / `debt_remaining` para refrescar sin recargar
import { useState } from 'react';
import { Banknote, Landmark, CreditCard } from 'lucide-react';
import { createPayment, PAY_METHOD_OPTIONS, money } from '../api';
import { useToast } from '../../../context/ToastContext';
import { useDebtFlags } from '../../../context/DebtFlagsContext';

/** Icono y color por método (Carlos 7-ago: "que sea el puro ícono", con color).
 *  El texto no se pierde: viaja en el `title` —lo que se lee al pasar el ratón— y en `aria-label`
 *  para lectores de pantalla. Se indexa por el `v` de PAY_METHOD_OPTIONS para que añadir un
 *  método allá no rompa esto.
 *  El color es la única pista cuando no hay texto, así que cada uno tiene su tono propio y se
 *  declara para claro Y oscuro: en tema oscuro el 600 se apaga demasiado para distinguirse. */
const METODO: Record<string, { icon: typeof Banknote; color: string }> = {
  cash:       { icon: Banknote,   color: 'text-emerald-600 dark:text-emerald-400' }, // billete, verde
  wire:       { icon: Landmark,   color: 'text-sky-600 dark:text-sky-400' },         // banco, azul
  creditcard: { icon: CreditCard, color: 'text-violet-600 dark:text-violet-400' },   // tarjeta, morado
};

/** Fecha de HOY en horario local. Nunca `new Date().toISOString()`: en México (UTC-7)
 *  eso adelanta el día a partir de las 17:00 y el pago cae en el corte de mañana. */
function todayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

interface Props {
  /** Adeudo a saldar. El backend valida el saldo real; aquí sólo se propone. */
  debtId: number;
  /** Saldo pendiente YA descontados los abonos previos (no el importe original del adeudo). */
  remaining: number;
  /** Tipo del adeudo ('membership', 'license', …) para que el pago herede el concepto correcto. */
  debtType?: string | null;
  /** Mes que cubre, para que el pago no caiga entero en el mes en curso. */
  month?: string | null;
  /** Concepto legible ("Cuota agosto 2026"), sólo para el texto de confirmación. */
  concept?: string | null;
  /** Se llama tras cobrar bien; el padre recarga su lista. */
  onPaid: () => void;
  /** Abre PaymentForm con este adeudo: montos distintos, descuento, otra fecha.
   *  OPCIONAL: si no se pasa, no se dibuja el botón "Otro…". */
  onOther?: () => void;
}

export default function QuickPay({ debtId, remaining, debtType, month, concept, onPaid, onOther }: Props) {
  const toast = useToast();
  // La insignia de adeudo del avatar sale de un mapa que se pide UNA vez para toda la consola
  // (DebtFlagsContext). Al cobrar desde aquí ese mapa no se entera solo: sin este refresh la
  // moneda seguiría encima del alumno hasta cambiar de pestaña. Aviso de la sesión que construyó
  // el componente, 7-ago-2026.
  const { refresh: refrescarInsignias } = useDebtFlags();
  // Guarda el método en curso (no un booleano): así sólo se apaga el botón pulsado y se ve
  // cuál se está cobrando. null = ninguno en vuelo.
  const [enVuelo, setEnVuelo] = useState<string | null>(null);

  async function cobrar(method: string) {
    if (enVuelo) return;                 // doble clic = doble pago; el candado va aquí
    if (!(remaining > 0)) { toast.error('Este adeudo ya no tiene saldo'); return; }
    setEnVuelo(method);
    try {
      const r = await createPayment({
        debt_id: debtId,
        type: debtType || 'membership',
        method,
        amount: remaining,
        pay_date: todayLocal(),
        months: month ? String(month).slice(0, 7) : null,
        assoc_name: concept || null,
      });
      // El backend decide si quedó saldado: puede haber entrado otro abono entre medias.
      toast.success(r.debt_settled
        ? `Cobrado ${money(remaining)} · adeudo saldado`
        : `Cobrado ${money(remaining)} · quedan ${money(Number(r.debt_remaining) || 0)}`);
      refrescarInsignias();   // que la moneda del avatar se caiga en el acto, no al cambiar de pestaña
      onPaid();
    } catch (e: any) {
      toast.error(e?.message || 'No se pudo registrar el pago');
    } finally {
      setEnVuelo(null);
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {PAY_METHOD_OPTIONS.map((o) => {
        const { icon: Icono, color } = METODO[o.v] || { icon: Banknote, color: 'text-content-2' };
        const cobrando = enVuelo === o.v;
        // Tooltip = sólo el método (Carlos 7-ago): el monto ya está a la vista en el mismo
        // renglón, repetirlo era decir dos veces lo mismo.
        return (
          <button key={o.v} type="button" disabled={!!enVuelo} onClick={() => cobrar(o.v)}
            aria-label={o.label}
            title={o.label}
            className={`rounded-lg border p-2 transition disabled:cursor-not-allowed disabled:opacity-50
              ${cobrando ? 'border-accent-solid bg-accent-solid/15' : 'border-line bg-surface hover:border-accent-solid hover:bg-accent-solid/10'}`}>
            {/* animate-pulse en vez de un spinner: el icono ya ocupa su sitio y no salta el layout */}
            <Icono size={17} className={`${color} ${cobrando ? 'animate-pulse' : ''}`} />
          </button>
        );
      })}
      {/* "Otro…" sólo si el padre lo pide (Carlos 7-ago: sobra donde ya existe otra puerta al
          modal — la manita de Adeudos o el botón "Registrar pago" de la ficha). */}
      {onOther && (
        <button type="button" disabled={!!enVuelo} onClick={onOther}
          title="Monto distinto, descuento, otra fecha o abono parcial"
          className="rounded-lg px-2 py-1.5 text-xs font-medium text-content-3 transition
                     hover:text-accent-ink disabled:opacity-50">
          Otro…
        </button>
      )}
    </div>
  );
}
